export type DashboardPresetId = 'ceo' | 'sales' | 'pm' | 'dev' | 'creative' | 'flatmedia';

export type DashboardWidgetId =
  | 'stats'
  | 'focus'
  | 'pipelineBoard'
  | 'week'
  | 'revenueGoal'
  | 'telegramLeads'
  | 'savedViews'
  | 'workforce'
  | 'burndown'
  | 'pipelines'
  | 'activity'
  | 'calendar'
  | 'actions'
  | 'notifications'
  | 'openTasks'
  | 'attentionQueue'
  | 'myDay'
  | 'portfolio'
  | 'budget'
  | 'agents'
  | 'trends'
  | 'swarm'
  | 'heatmap'
  | 'pipeburn'
  | 'clock'
  | 'notes'
  | 'nexus'
  | 'island'
  | 'quote'
  | 'flatmedia';

export interface DashboardPreset {
  id: DashboardPresetId;
  label: string;
  widgets: readonly DashboardWidgetId[];
}

export interface DashboardWidgetDefinition {
  id: DashboardWidgetId;
  label: string;
  span: number;
  category: 'overview' | 'pipeline' | 'operations' | 'erp2';
  available: boolean;
}

// Supported widget widths on desktop (in 12-column grid units). Mobile always
// collapses to a single column, so these options only affect the desktop view.
export type DashboardWidgetSpan = 4 | 6 | 8 | 12;

export const DASHBOARD_WIDGET_SPAN_OPTIONS: readonly DashboardWidgetSpan[] = [4, 6, 8, 12] as const;

// Версия поднята до v9: раскладка теперь хранит не только порядок виджетов,
// но и опциональную ширину каждого виджета (`spanOverride`). Смена ключа
// возвращает раскладку к предустановке; ради этого версия в ключе и заведена.
export const DASHBOARD_LAYOUT_STORAGE_KEY = 'govardos-dashboard-layout-v9';

// Предыдущий ключ используется как fallback при первом чтении, чтобы не
// терять пользовательскую раскладку при миграции v8 → v9.
export const DASHBOARD_LAYOUT_STORAGE_KEY_V8 = 'govardos-dashboard-layout-v8';
export const DEFAULT_DASHBOARD_PRESET_ID: DashboardPresetId = 'pm';

export const DASHBOARD_PRESETS: readonly DashboardPreset[] = [
  // «Мои представления» стоят сразу за строкой фокуса дня. Именно за строкой, а
  // не за самим виджетом: `focus` делит ряд с соседями, и вставка широкого
  // виджета между ними разорвала бы пару и перетасовала всё, что ниже.
  {
    id: 'ceo',
    label: 'CEO',
    // Only widgets that actually render: the surface must not select a preset
    // whose entries are unavailable placeholders (it used to show four widgets
    // out of ten). Portfolio/budget/trends stay in the registry for a later
    // release.
    widgets: [
      'stats',
      'pipelines',
      'burndown',
      'focus',
      'activity',
      'attentionQueue',
      'notifications',
      'savedViews',
    ],
  },
  {
    id: 'sales',
    label: 'Sales',
    widgets: [
      'stats',
      'myDay',
      'pipelines',
      'focus',
      'calendar',
      'activity',
      'notifications',
      'attentionQueue',
      'openTasks',
      'actions',
      'savedViews',
    ],
  },
  {
    id: 'pm',
    label: 'PM',
    // Prototype D composition: full-width KPI → focus (5) + mini-kanban (7) →
    // revenue (8) + telegram leads (4) → My day (7) + workforce (4) → weekly
    // ribbon → saved views → burndown (8) + pipelines (4) → activity (8) +
    // notifications (4) side by side → open tasks, calendar and actions below.
    widgets: [
      'stats',
      'focus',
      'pipelineBoard',
      'week',
      'revenueGoal',
      'telegramLeads',
      'myDay',
      'workforce',
      'savedViews',
      'burndown',
      'pipelines',
      'activity',
      'notifications',
      'attentionQueue',
      'openTasks',
      'calendar',
      'actions',
    ],
  },
  {
    id: 'dev',
    label: 'Dev',
    widgets: [
      'focus',
      'activity',
      'savedViews',
      'openTasks',
      'agents',
      'pipelines',
      'pipeburn',
      'actions',
      'clock',
      'notes',
    ],
  },
  {
    id: 'creative',
    label: 'Design',
    widgets: [
      'stats',
      'savedViews',
      'nexus',
      'heatmap',
      'island',
      'quote',
      'openTasks',
      'trends',
      'clock',
      'notes',
    ],
  },
  {
    id: 'flatmedia',
    label: 'Flatmedia',
    widgets: [
      'flatmedia',
      'portfolio',
      'activity',
      'notifications',
      'savedViews',
      'pipelines',
      'notes',
    ],
  },
] as const;

export const WIDGET_REGISTRY: readonly DashboardWidgetDefinition[] = [
  { id: 'stats', label: 'stats', span: 12, category: 'overview', available: true },
  // Спаны — это ВЕС виджета в строке, а не жёсткая ширина: страница нормализует
  // каждую строку к полным 12 колонкам (см. DashboardPage). Примеры пар для
  // дефолтного PM preset: focus(5)+pipelineBoard(7)=12; myDay(7)+workforce(4)
  // → 8+4; activity(8)+notifications(4)=12. В Sales/CEO и кастомных раскладках
  // недостающие колонки добираются у широкого виджета, чтобы строка всегда
  // заполнялась до края.
  { id: 'focus', label: 'focus', span: 5, category: 'overview', available: true },
  { id: 'pipelineBoard', label: 'funnel board', span: 7, category: 'pipeline', available: true },
  { id: 'week', label: 'week', span: 12, category: 'operations', available: true },
  { id: 'revenueGoal', label: 'revenue goal', span: 8, category: 'overview', available: true },
  {
    id: 'telegramLeads',
    label: 'telegram leads',
    span: 4,
    category: 'operations',
    available: true,
  },
  // Во всю ширину: внутри лежат плитки представлений, и их число задаёт человек.
  { id: 'savedViews', label: 'saved views', span: 12, category: 'overview', available: true },
  { id: 'workforce', label: 'workforce', span: 4, category: 'operations', available: true },
  { id: 'burndown', label: 'burndown', span: 8, category: 'pipeline', available: true },
  { id: 'pipelines', label: 'pipelines', span: 4, category: 'pipeline', available: true },
  // Деньги под риском: пары с openTasks (8+4) в PM/Sales пресетах, поэтому
  // дефолтный вес — 8, а не полная строка.
  {
    id: 'attentionQueue',
    label: 'attention queue',
    span: 8,
    category: 'pipeline',
    available: true,
  },
  { id: 'activity', label: 'activity', span: 8, category: 'operations', available: true },
  { id: 'calendar', label: 'calendar', span: 4, category: 'operations', available: true },
  { id: 'actions', label: 'actions', span: 4, category: 'operations', available: true },
  { id: 'notifications', label: 'notifications', span: 4, category: 'operations', available: true },
  { id: 'openTasks', label: 'open tasks', span: 4, category: 'operations', available: true },
  { id: 'myDay', label: 'my day', span: 7, category: 'overview', available: true },
  { id: 'portfolio', label: 'portfolio', span: 8, category: 'erp2', available: false },
  { id: 'budget', label: 'budget', span: 4, category: 'erp2', available: false },
  { id: 'agents', label: 'agents', span: 4, category: 'erp2', available: false },
  { id: 'trends', label: 'trends', span: 8, category: 'erp2', available: false },
  { id: 'swarm', label: 'swarm', span: 4, category: 'erp2', available: false },
  { id: 'heatmap', label: 'heatmap', span: 4, category: 'erp2', available: false },
  { id: 'pipeburn', label: 'pipeburn', span: 4, category: 'erp2', available: false },
  { id: 'clock', label: 'clock', span: 4, category: 'erp2', available: false },
  { id: 'notes', label: 'notes', span: 4, category: 'operations', available: true },
  { id: 'nexus', label: 'nexus', span: 8, category: 'erp2', available: false },
  { id: 'island', label: 'island', span: 4, category: 'erp2', available: false },
  { id: 'quote', label: 'quote', span: 4, category: 'erp2', available: false },
  { id: 'flatmedia', label: 'flatmedia', span: 8, category: 'erp2', available: false },
] as const;

const PRESET_IDS = new Set<DashboardPresetId>(DASHBOARD_PRESETS.map((preset) => preset.id));
const WIDGET_IDS = new Set<DashboardWidgetId>(WIDGET_REGISTRY.map((widget) => widget.id));

export function isDashboardPresetId(value: string): value is DashboardPresetId {
  return PRESET_IDS.has(value as DashboardPresetId);
}

export function isDashboardWidgetId(value: string): value is DashboardWidgetId {
  return WIDGET_IDS.has(value as DashboardWidgetId);
}

export function getPresetWidgetIds(presetId: DashboardPresetId): DashboardWidgetId[] {
  return [...DASHBOARD_PRESETS.find((preset) => preset.id === presetId)!.widgets];
}

export function getPresetLabel(
  presetId: DashboardPresetId,
  t?: (key: string, params?: Record<string, string | number>) => string,
): string {
  if (t) {
    const key = `crm.dashboard.presets.${presetId}`;
    const translated = t(key);
    if (translated && translated !== key) return translated;
  }
  const preset = DASHBOARD_PRESETS.find((p) => p.id === presetId);
  return preset?.label ?? presetId;
}

export function getWidgetDefinition(
  widgetId: DashboardWidgetId,
): DashboardWidgetDefinition | undefined {
  return WIDGET_REGISTRY.find((widget) => widget.id === widgetId);
}

export function getAvailableDashboardWidgets(): DashboardWidgetDefinition[] {
  return WIDGET_REGISTRY.filter((widget) => widget.available);
}

export function getUnavailableDashboardWidgets(): DashboardWidgetDefinition[] {
  return WIDGET_REGISTRY.filter((widget) => !widget.available);
}

export function isDashboardWidgetSpan(value: number): value is DashboardWidgetSpan {
  return (DASHBOARD_WIDGET_SPAN_OPTIONS as readonly number[]).includes(value);
}

/**
 * Map an arbitrary registry span to the nearest supported widget span option.
 * Deterministic tie-break: round down to the next smaller option. This keeps
 * default presets visually unchanged when possible (5→4, 7→8, 8→8, 12→12).
 */
export function normalizeWidgetSpan(span: number): DashboardWidgetSpan {
  if (span <= 5) return 4;
  if (span === 6) return 6;
  if (span <= 11) return 8;
  return 12;
}
